import type { Ref } from 'vue'

import { onBeforeUnmount, ref, watch } from 'vue'

import useResizeObserver from './use-resize-observer'
import useSetCSSVariable from './use-set-css-variable'

/**
 * A Vue composable that sets a CSS variable to the height of an element.
 * @param elementToMeasure - The element to measure the height of.
 * @param key - The CSS variable key to set.
 * @param elementToSetVariableOn - The element to set the CSS variable on. Defaults to document.
 * @param unit - The unit to set the CSS variable to. Defaults to px.
 */
const useSetCssHeightVariable = (
  elementToMeasure: Ref<HTMLElement | null>,
  key: string,
  elementToSetVariableOn?: HTMLElement,
  unit = 'px'
): void => {
  const height = ref(0)
  let animationFrameId: number | null = null

  useSetCSSVariable(key, height, elementToSetVariableOn, unit)

  const { entries } = useResizeObserver(elementToMeasure)

  onBeforeUnmount(() => {
    if (animationFrameId) {
      window.cancelAnimationFrame(animationFrameId)
    }
  })

  watch(entries, (newValue) => {
    if (newValue.length > 0) {
      const updatedHeight =
        newValue[newValue.length - 1]?.contentRect?.height || 0

      animationFrameId = window.requestAnimationFrame(() => {
        height.value = updatedHeight
      })
    }
  })
}

export default useSetCssHeightVariable
